import { useMemo, useState } from "react";
import { useData } from "../data/DataContext";
import { ObjectiveModal } from "../components/ObjectiveModal";
import {
  AddObjectiveCard,
  ObjectiveCard,
  RagCountRing,
  buildRagIndex,
  countRags,
  getCurrentRag,
  getHistoryScores,
  reportingYearForDate,
} from "../components/ObjectiveCard";
import { createMonthLock, deleteMonthLock } from "../data/api";
import type { Objective } from "../data/types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CURRENT_MONTH_IDX = new Date().getMonth();

type ModalState =
  | { kind: "none" }
  | { kind: "edit"; objective: Objective }
  | { kind: "create" };

export function MyObjectivesPage() {
  const { objectives, ragScores, monthLocks, categories: taxonomyCategories, me, refresh } = useData();
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [lockBusy, setLockBusy] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  const myName = me.matchedPerson?.fullName ?? null;
  const reportingYear = reportingYearForDate();

  // Sort order is the taxonomy's insertion order; categories not in taxonomy
  // sink to the bottom. Stable tiebreak on objective id.
  const categoryOrder = useMemo(
    () => taxonomyCategories.map((c) => c.name),
    [taxonomyCategories],
  );
  const categoryIndex = useMemo(() => {
    const m = new Map<string, number>();
    categoryOrder.forEach((c, i) => m.set(c, i));
    return m;
  }, [categoryOrder]);

  const myObjectives = useMemo(() => {
    if (!myName) return [];
    return objectives
      .filter((o) => o.personName === myName && o.status !== "Deleted")
      .sort((a, b) => {
        const ai = a.category ? categoryIndex.get(a.category) ?? 99 : 99;
        const bi = b.category ? categoryIndex.get(b.category) ?? 99 : 99;
        if (ai !== bi) return ai - bi;
        return (a.id ?? 0) - (b.id ?? 0);
      });
  }, [objectives, myName, categoryIndex]);

  const categories = useMemo(() => {
    // Union of taxonomy categories and any in-use-but-not-yet-in-taxonomy
    // categories. Taxonomy order wins; stragglers go to the end alphabetically.
    const inUse = new Set<string>();
    for (const o of objectives) if (o.category) inUse.add(o.category);
    const taxonomy = new Set(categoryOrder);
    const extras = [...inUse].filter((c) => !taxonomy.has(c)).sort();
    return [...categoryOrder, ...extras];
  }, [objectives, categoryOrder]);

  const ragIndex = useMemo(
    () => buildRagIndex(ragScores, reportingYear),
    [ragScores, reportingYear],
  );

  const counts = useMemo(() => countRags(myObjectives, ragIndex), [myObjectives, ragIndex]);

  // Lock pill state. The pill represents the SIGNED-IN user's own row for
  // the CURRENT calendar month within the current reporting year. A row in
  // monthLocks for that exact (personId, year, month) means the user has
  // signalled "I'm done editing this month"; it gates RAG-score writes
  // server-side via the 423 LOCK_RESPONSE check in api/src/functions/
  // rag-scores.ts and surfaces visually here as a brass-tinted closed-lock
  // pill at the top of the page.
  const currentCalMonth = new Date().getMonth() + 1;
  const currentLock = useMemo(() => {
    if (!me.matchedPerson) return null;
    return (
      monthLocks.find(
        (l) =>
          l.personId === me.matchedPerson!.id &&
          l.year === reportingYear &&
          l.month === currentCalMonth,
      ) ?? null
    );
  }, [monthLocks, me.matchedPerson, reportingYear, currentCalMonth]);
  const isLocked = currentLock != null;

  if (!me.matchedPerson) {
    return (
      <div className="rounded-lg border border-rule bg-card p-8 shadow-card">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
          Heads up
        </div>
        <h2 className="mb-3 font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
          We couldn't match you to a person record
        </h2>
        <p className="mb-2 max-w-xl text-[14px] leading-[1.6] text-ink-2">
          You're signed in as{" "}
          <span className="font-medium text-ink">{me.displayName}</span>, but no entry in the
          People list matches that name.
        </p>
        <p className="max-w-xl text-[14px] leading-[1.6] text-ink-2">
          Once the SharePoint to SQL data migration runs (Step 8), the People list will be
          populated and you'll see your objectives here. In the meantime,{" "}
          <a href="/diag" className="text-nymr-navy underline hover:opacity-80">
            /diag
          </a>{" "}
          shows what's loaded.
        </p>
      </div>
    );
  }

  const canAdd = me.canEditFor(me.matchedPerson.fullName);
  const yearLabel = `${reportingYear}/${(reportingYear + 1) % 100}`;

  const toggleLock = async () => {
    if (lockBusy || !me.matchedPerson) return;
    setLockBusy(true);
    setLockError(null);
    try {
      if (currentLock) {
        await deleteMonthLock(currentLock.id);
      } else {
        await createMonthLock({
          PersonName: me.matchedPerson.fullName,
          Year: reportingYear,
          Month: currentCalMonth,
        });
      }
      await refresh();
    } catch (err) {
      setLockError(err instanceof Error ? err.message : String(err));
    } finally {
      setLockBusy(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="mb-[26px] flex items-start justify-between gap-[30px] rounded-lg border border-rule bg-card px-[26px] py-[22px] shadow-card">
        <div>
          <h1 className="font-display text-[28px] font-medium leading-[1.15] tracking-[-0.015em]">
            My <em className="font-normal italic text-brass">{yearLabel}</em> objectives
          </h1>
          <div className="mt-[6px] font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            Status &middot; {MONTHS[CURRENT_MONTH_IDX]} {reportingYear} &middot;{" "}
            {me.matchedPerson.fullName}
            {me.matchedPerson.department ? ` · ${me.matchedPerson.department}` : ""}
          </div>
        </div>
        <div className="self-stretch w-px bg-rule" />
        <div className="flex-shrink-0 text-right">
          <RagCountRing counts={counts} />
          <div className="mt-2 font-mono text-[10px] tracking-[0.04em] text-ink-3">
            {myObjectives.length} objective{myObjectives.length === 1 ? "" : "s"}
          </div>
        </div>
      </section>

      {/* Lock pill - controls the current calendar month for the signed-in
          user only. Locked = no RAG saves on that month go through (blocked
          client-side in ObjectiveModal and server-side in rag-scores.ts).
          Brass tinting matches the "current period" accent used elsewhere. */}
      <div className="mb-[18px] flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={toggleLock}
          disabled={lockBusy}
          aria-pressed={isLocked}
          title={
            isLocked
              ? `${MONTHS[CURRENT_MONTH_IDX]} ${reportingYear} is locked. Click to unlock and resume editing.`
              : `Click to lock ${MONTHS[CURRENT_MONTH_IDX]} ${reportingYear} when you're done editing it.`
          }
          className={[
            "inline-flex items-center gap-[8px] rounded-full border px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
            isLocked
              ? "border-brass bg-[rgb(185_138_58/0.12)] text-brass hover:bg-[rgb(185_138_58/0.18)]"
              : "border-rule-strong bg-card text-ink-2 hover:border-ink-3 hover:text-ink",
            lockBusy ? "opacity-60 cursor-wait" : "cursor-pointer",
          ].join(" ")}
        >
          {isLocked ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" transform="translate(8 -3) rotate(35 12 7)" />
            </svg>
          )}
          <span>
            {isLocked ? `${MONTHS[CURRENT_MONTH_IDX]} ${reportingYear} - locked` : `Lock ${MONTHS[CURRENT_MONTH_IDX]} ${reportingYear}`}
          </span>
        </button>
        {lockError ? (
          <span className="text-[11.5px] text-rag-red">{lockError}</span>
        ) : isLocked ? (
          <span className="text-[11.5px] text-ink-3">Tell others you're done with this month. Saves on this month are blocked until unlocked.</span>
        ) : null}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 gap-[18px]">
        {myObjectives.map((o) => (
          <ObjectiveCard
            key={o.id}
            objective={o}
            rag={getCurrentRag(o, ragIndex)}
            scores={getHistoryScores(o, ragIndex)}
            reportingYear={reportingYear}
            editable={me.canEdit(o)}
            onClick={() => setModal({ kind: "edit", objective: o })}
          />
        ))}
        {canAdd ? <AddObjectiveCard onClick={() => setModal({ kind: "create" })} /> : null}
      </div>

      {/* Modals */}
      {modal.kind === "edit" ? (
        <ObjectiveModal
          mode="edit"
          objective={modal.objective}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
      {modal.kind === "create" && me.matchedPerson ? (
        <ObjectiveModal
          mode="create"
          personName={me.matchedPerson.fullName}
          defaultYear={reportingYear}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
    </div>
  );
}
